import { useEffect, useRef, useState } from "react";
import type {
  RescueCase,
  UpdateRescueCommitmentRequest,
} from "@io-spoke/contracts/rescue";
import { Button } from "@components/ui/button";
import { RescueConversation } from "./RescueConversation";
import { RescueCaseBriefing } from "./RescueCaseBriefing";
import { RescueGuideChoices, type RescueGuideView } from "./RescueGuideChoices";
import { RecoveryPlanAnswer } from "./RecoveryPlanAnswer";
import { PromiseLedger } from "./PromiseLedger";
import { RescueCustomerSummary } from "./RescueCustomerSummary";
import { RescueEvidenceCards } from "./RescueEvidenceCards";

type Props = {
  rescueCase: RescueCase;
  onRecoveryCompleted?: (entityId: string) => void;
  updatingCommitmentId: string | null;
  onOpenWork: (id: string) => void | Promise<void>;
  onUpdateCommitment: (
    commitmentId: string,
    update: UpdateRescueCommitmentRequest,
  ) => void | Promise<void>;
};

export function RescueCaseFile(props: Props) {
  return <GuidedCaseFile key={props.rescueCase.id} {...props} />;
}

function GuidedCaseFile({
  rescueCase,
  onRecoveryCompleted,
  updatingCommitmentId,
  onOpenWork,
  onUpdateCommitment,
}: Props) {
  const [view, setView] = useState<RescueGuideView | null>(() => {
    const slack = new URLSearchParams(window.location.search).get("slack");
    if (
      window.location.pathname ===
        `/rescues/${encodeURIComponent(rescueCase.id)}` &&
      ["connected", "error"].includes(slack ?? "")
    )
      return "investigation";
    return null;
  });
  const [sourceSelection, setSourceSelection] = useState<{ id: string } | null>(
    null,
  );
  const evidenceRegion = useRef<HTMLDivElement>(null);
  const planChoice = useRef<HTMLButtonElement>(null);
  const evidence = useRef(rescueCase.evidence);
  evidence.current = rescueCase.evidence;

  useEffect(() => {
    const openLinkedEvidence = () => {
      let anchor: string;
      try {
        anchor = decodeURIComponent(window.location.hash.slice(1));
      } catch {
        return;
      }
      if (evidence.current.some((item) => item.id === anchor)) {
        setView("evidence");
        setSourceSelection({ id: anchor });
      }
    };
    openLinkedEvidence();
    window.addEventListener("hashchange", openLinkedEvidence);
    return () => window.removeEventListener("hashchange", openLinkedEvidence);
  }, []);

  useEffect(() => {
    if (view !== "evidence" || !sourceSelection) return;
    const target = document.getElementById(sourceSelection.id);
    if (
      !(target instanceof HTMLDetailsElement) ||
      !evidenceRegion.current?.contains(target)
    ) {
      return;
    }
    target.open = true;
    target.querySelector("summary")?.focus({ preventScroll: true });
    target.scrollIntoView?.({ block: "nearest", behavior: "auto" });
  }, [view, sourceSelection]);

  const chooseView = (next: RescueGuideView) => {
    setSourceSelection(null);
    setView(next);
  };

  const openSource = (id: string) => {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}#${encodeURIComponent(id)}`,
    );
    setView("evidence");
    setSourceSelection({ id });
    window.dispatchEvent(new HashChangeEvent("hashchange"));
  };

  return (
    <section
      aria-labelledby="guided-case-title"
      className="overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm"
    >
      <RescueCaseBriefing
        rescueCase={rescueCase}
        actions={
          <RescueGuideChoices
            view={view}
            onChoose={chooseView}
            planChoiceRef={planChoice}
          />
        }
        onOpenEvidence={openSource}
      />
      {view === "investigation" ? (
        <RescueConversation
          key={rescueCase.id}
          caseId={rescueCase.id}
          evidenceIds={rescueCase.evidence.map((item) => item.id)}
          onOpenEvidence={openSource}
          onReviewPlan={() => {
            chooseView("plan");
            planChoice.current?.focus();
          }}
        />
      ) : null}
      {view === "evidence" ? (
        <div
          ref={evidenceRegion}
          id="rescue-evidence-answer"
          role="region"
          aria-label="Evidence review"
        >
          <p className="px-5 pt-5 text-sm leading-6 sm:px-6">
            Here are the sources behind the assessment. Open one to check the
            exact wording.
          </p>
          <RescueEvidenceCards evidence={rescueCase.evidence} />
          <div className="px-5 py-5 sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              onClick={() => {
                setView("plan");
                planChoice.current?.focus();
              }}
            >
              Next: review recovery plan
            </Button>
          </div>
        </div>
      ) : null}
      {view === "plan" ? (
        <RecoveryPlanAnswer
          rescueCase={rescueCase}
          onCompleted={onRecoveryCompleted}
          onOpenWork={onOpenWork}
        />
      ) : null}
      {rescueCase.commitments.length > 0 ? (
        <details className="border-t">
          <summary className="min-h-11 cursor-pointer rounded px-5 py-4 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-6">
            Manage promises · {rescueCase.commitments.length}
          </summary>
          <PromiseLedger
            rescueCase={rescueCase}
            updatingId={updatingCommitmentId}
            onUpdate={onUpdateCommitment}
          />
        </details>
      ) : null}
      <details className="border-t">
        <summary className="min-h-11 cursor-pointer rounded px-5 py-4 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-6">
          Account and assessment details
        </summary>
        <RescueCustomerSummary rescueCase={rescueCase} />
      </details>
    </section>
  );
}
